import { Link } from "react-router-dom";
import { AsciiField, AsciiBanner, Button, Pipeline, Stat, Nav } from "../components/ascii";
import SEO, { SEO_CONFIG } from "../components/SEO";
import graspdLogo from "../assets/graspd-logo.svg";
import "./Home.css";

// SAMPLE COPY (draft for review). Rationale in docs/academy/COPY-BRIEF.md. Figures come from docs/academy/RESEARCH.md; only rows
// marked [primary] there are used here. Same rules as Home: no em dashes, no invented
// metrics, mono UI text lowercase. Do not claim Academy outcomes: we have no data yet.

const NAV_LINKS = [
  { label: "the problem", href: "#problem" },
  { label: "evidence", href: "#evidence" },
  { label: "g-score", href: "#g-score" },
  { label: "families", href: "#families" },
  { label: "enterprise", href: "/", site: true },
  { label: "academy", href: "/academy", site: true, current: true },
];

const START_HREF = "/login?signup=true";

// Verified against ai-learn-api (cognition/assessment/instruments, Pillar.SURFACED): six pillars, twelve tests.
// docs/g-score-api-contract.md predates the memory and problem-solving instruments and is stale on this point.
const PILLARS = [
  ["Speed of processing", "Digit symbol, pattern comparison"],
  ["Attention", "Flanker, Stroop"],
  ["Memory", "Corsi span, delayed recognition"],
  ["Flexibility", "Cued task switching, Navon switching"],
  ["Problem solving", "Matrix reasoning, Tower of London"],
  ["Spatial reasoning", "Mental rotation, paper folding"],
];

const LOOP = [
  { name: "Test", detail: "A short battery for each pillar sets your G-Score on a 0 to 100 scale. Your first test is the baseline, at 50. It repeats every two weeks." },
  { name: "Train", detail: "Daily sessions of a few minutes, matched to your level, with a streak to keep the habit." },
  { name: "Test again", detail: "Training never changes your score. Only the next test does, so the number shows how far you have moved from your own baseline." },
];

const LEARN = [
  ["Courses from any topic or PDF", "Lessons, notes and a schedule, built for the thing you want to learn."],
  ["Quizzes that make you answer", "Knowledge checks and quiz chats grade what you actually know, not what you read."],
  ["A review queue for what fades", "Spaced repetition brings back topics just before you would forget them."],
  ["Ask Graspd, in context", "Highlight any passage in a lesson and ask about it without leaving the page."],
];

// Parent Portal facts: CLAUDE.md (supervision code, assign via the same pipeline, transcript by subject and
// academic year, supervisors free). The portal does not show G-Score today, so the copy does not say it does.
const FAMILY = [
  ["Assign a course in minutes", "Describe a topic or upload a PDF and the course is built for your child, with lessons, quizzes and a schedule."],
  ["Every child in one place", "Link each learner with a code and follow their progress separately from the Parent Portal."],
  ["A transcript that writes itself", "Completed courses roll up by subject and academic year, ready to print when you need records."],
  ["Free for the grown-up", "Learners subscribe. Supervising them, as a parent, tutor or mentor, is always free."],
];

const FOOTER_LINKS = [
  { label: "enterprise", href: "/" },
  { label: "the problem", href: "#problem" },
  { label: "evidence", href: "#evidence" },
  { label: "g-score", href: "#g-score" },
  { label: "families", href: "#families" },
  { label: "privacy", to: "/privacy" },
  { label: "terms", to: "/terms" },
];

export default function Academy() {
  return (
    <div className="hp gd-root">
      <SEO {...SEO_CONFIG.academy} />
      <Nav links={NAV_LINKS} cta="start training" ctaHref={START_HREF} logo={graspdLogo} />

      <section className="hp-hero-sec">
        <div className="wrap hp-hero">
          <div>
            <h1 className="hp-h1">The tools got smarter.<br />Stay <span>sharper</span>.</h1>
            <p className="hp-lede">Graspd Academy is a gym for the part of you that thinks. Train six pillars of thinking in short daily sessions, and track your progress with a score that only real tests can move.</p>
            <div className="hp-ctas">
              <Button size="lg" arrow href={START_HREF}>start training</Button>
              <Button size="lg" variant="secondary" brackets href="#evidence">see the evidence</Button>
            </div>
          </div>
          <div className="hp-fig">
            <AsciiField mode="focus" cols={66} rows={36} phase={6.28} />
            <div className="hp-cap"><span>fig.01  focus, locking on</span><span /></div>
          </div>
        </div>
      </section>

      <section id="problem">
        <div className="wrap">
          <h2 className="hp-h2">You haven&rsquo;t lost it. You&rsquo;ve stopped using it.</h2>
          <p className="hp-body">Think of the last time you reached for the assistant before you had even tried. The email. The summary. The first draft. The formula you used to know cold. It was faster, it was fine, and it felt like nothing.</p>
          <p className="hp-body">That is what makes this hard to see. Nothing breaks. There is no day it stops working. Skills fade the way fitness does: quietly, from disuse, until the moment you need one and it is not there.</p>
          <p className="hp-body">Maybe you have felt a small version of it already. The word that will not come. The long read you cannot finish. The answer you accepted because checking felt like work. AI that can act for us is only getting more capable, so the pull to hand it all over is only getting stronger.</p>
        </div>
      </section>

      <section id="evidence">
        <div className="wrap">
          <h2 className="hp-h2">Faster today. Weaker later.</h2>
          <p className="hp-body">Researchers are starting to measure what happens when people hand the thinking to AI. Three studies, three settings, one pattern. They are early and not all of them are peer reviewed, so read each as a signal, not a verdict.</p>
          <div className="hp-stats">
            <Stat value="50 vs 67" unit="%" label="Engineers learned an unfamiliar coding library, then took a quiz. Those who used an AI assistant scored 50%. Those who did not scored 67%. The widest gap was in debugging, the skill of noticing that code is wrong." source="Shen & Tamkin, Anthropic (randomised, n=52)" />
            <Stat value="-17" unit="%" label="High-school students practised maths with an unrestricted AI helper. On the later exam, without it, they scored 17% below classmates who never had it. Safeguards built into a second tutor largely prevented that loss." source="Bastani et al., PNAS 2025" />
            <Stat value="25" unit="%" label="On one maths learning platform, students' odds of answering a proctored question correctly fell 25% after ChatGPT arrived. Their study time fell 31%. Preprint." source="Rismanchian et al., arXiv 2026 (3.2M interactions)" />
          </div>
          <p className="hp-body" style={{ marginTop: "var(--space-8)" }}>Same pattern each time: the task gets easier with the AI, and the ability to do it without the AI slips. In the coding study, spotting a wrong answer was the weakest skill, and it is the one you most need when a tool sounds sure and is mistaken. AI is not the problem. How you use it decides what you keep.</p>
          <p className="hp-body">The way back is practice that cannot be handed off, and a way to see whether you are improving. That is Graspd Academy.</p>
        </div>
      </section>

      <section id="g-score">
        <div className="wrap">
          <div className="hp-split">
            <div>
              <h2 className="hp-h2">Measure it. Train it. Measure it again.</h2>
              <p className="hp-body">Most apps tell you how long you spent. The G-Score tells you how your mind performs, from short tests across six pillars of thinking. These are the kinds of tasks cognitive psychologists use in the lab.</p>
              <p className="hp-body">Your score comes only from the test. Practice sessions never touch it, so you cannot drift your way to a better number. You get better at the task, or you do not.</p>
            </div>
            <div className="hp-panel">
              <div className="hp-panel-h"><b>what the test measures</b></div>
              {PILLARS.map(([name, tasks]) => (
                <div key={name} className="ac-row"><b>{name}</b><span>{tasks}</span></div>
              ))}
            </div>
          </div>
          <Pipeline steps={LOOP} />
        </div>
      </section>

      <section id="learn">
        <div className="wrap">
          <h2 className="hp-h2">Learning that makes you think.</h2>
          <p className="hp-body">Academy sits alongside Graspd&rsquo;s course engine, so the habit carries into what you study. AI builds the material. You still have to retrieve it.</p>
          <div className="hp-who hp-who-plain">
            {LEARN.map(([name, detail]) => (
              <div key={name}><b>{name}</b><span>{detail}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section id="families">
        <div className="wrap">
          <h2 className="hp-h2">Built for homeschool families, too.</h2>
          <p className="hp-body">AI can finish the worksheet. Your child still has to be able to do it alone. Graspd gives you a way to teach with AI and see what they actually keep, and your child can take the G-Score and train on their own account.</p>
          <div className="hp-who hp-who-plain">
            {FAMILY.map(([name, detail]) => (
              <div key={name}><b>{name}</b><span>{detail}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section id="start" className="hp-cta">
        <div className="wrap">
          <h2 className="hp-h2">Start with one hard thing.</h2>
          <p className="hp-body">Take the first test to set your G-Score. Then train a few minutes a day and see how far you have moved in two weeks.</p>
          <div className="hp-ctas">
            <Button size="lg" arrow href={START_HREF}>start training</Button>
            <Button size="lg" variant="secondary" brackets href={START_HREF}>set up your family</Button>
          </div>
        </div>
      </section>

      <footer className="hp-foot">
        <div className="wrap">
          <AsciiBanner reveal duration={1600} />
          <div className="hp-foot-row">
            <div>
              {FOOTER_LINKS.map((l) =>
                l.to ? <Link key={l.label} to={l.to}>{l.label}</Link> : <a key={l.label} href={l.href}>{l.label}</a>
              )}
            </div>
            <span>© 2026 graspd</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
